/**
 * @fileoverview API client for WebSocket command broadcasting
 * Handles sending commands to the backend which then broadcasts them via WebSocket
 */

import apiClient from './client';

/**
 * Send a chart control command via HTTP API
 * The backend will send this to a specific dashboard connection
 * 
 * @param {Object} command - Chart command object
 * @param {string} command.elementId - ID of element to control
 * @param {string} command.action - Action to perform (scale, move, reset, center, minimize)
 * @param {Object} command.dimensions - Width and height values
 * @param {Object} command.position - Top and left position values
 * @param {number} command.zIndex - Z-index value
 * @param {string} userId - User ID of target dashboard connection
 * @param {string} websocketId - WebSocket ID of target dashboard connection
 * @param {string} [channel='global'] - WebSocket channel (fallback for broadcast)
 * @returns {Promise} API response
 */
export const sendChartCommand = async (command, userId, websocketId, channel = 'global') => {
  try {
    const payload = {
      command,
      userId,
      websocketId,
      channel
    };
    
    const response = await apiClient.post('/api/websocket-commands/chart-command', payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Send a generic message via WebSocket broadcast
 * 
 * @param {string} type - Message type
 * @param {Object} [data] - Message data
 * @param {string} [channel='global'] - WebSocket channel to broadcast to
 * @returns {Promise} API response
 */
export const broadcastMessage = async (type, data, channel = 'global') => {
  try {
    const response = await apiClient.post('/api/websocket-commands/broadcast', {
      type,
      data,
      channel
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get list of active dashboard connections
 * 
 * @returns {Promise} Dashboard list with connection info
 */
export const getActiveDashboards = async () => {
  try {
    const response = await apiClient.get('/api/websocket-commands/dashboards');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Check health of WebSocket commands service
 * 
 * @returns {Promise} Health status
 */
export const getWebSocketCommandsHealth = async () => {
  try {
    const response = await apiClient.get('/api/websocket-commands/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};